// HelpAssistant/contentAvoidance.js
//
// T22.581 (Gordon, S23+ Chrome, VID-20261021-0917): "Phone: the floating close
// (x) and help (?) buttons on the right edge overlap page content on most
// pages (cards, buttons, lists)". MEASURED live on web-stg.iyuga.net
// (main.194c6a56.js, Chromium 384x832, Pro account) with the phone edge tab at
// its fixed corner 352,724-384,816:
//   /gallery        the tab sat on the "Create Wheel" button
//   /discover       the tab sat on a card's "Add to favorites" heart
//   /notifications  the tab sat on a notification row's text
// T22.227/T22.177 already shrank the tab to a 32px column flush to the edge;
// any fixed spot on that edge still lands on SOMETHING on most pages, because
// the page scrolls under it. Shrinking it again cannot fix that.
//
// The fix is to let the docked tab move along the edge (the only axis it is
// allowed to move on, see clampFabPosition) to the nearest spot where the
// content under its column is clear: no control and no text. Blank card
// background is fine to cover; a button, link, heart or line of text is not.
//
// Pure picker + a DOM sampler. The picker takes plain intervals so it is
// unit-testable; the sampler only reads the page (elementsFromPoint + text
// ranges), never writes to it.

/** Vertical sampling pitch along the column, px. */
export const SAMPLE_STEP = 4;

/** Candidate positions are tried every this many px. */
export const CANDIDATE_STEP = 4;

/** Controls that must never sit under the tab. */
export const CONTROL_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "select",
  "textarea",
  "label",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  // T22.633 (Gordon S23+ video 2026-10-21, web-stg wheel/510012): the phone
  // View Wheel's rim labels are tappable SVG pills (tap opens the segment)
  // with no role and no HTML text, so the tab parked its dismiss X on
  // "User Exp..." (measured 384x740: X 352..384 x 632..676, pill 288..368 x
  // 616..642). The renderer marks every tappable pill data-interactive.
  '.rv2-pill[data-interactive="true"]',
].join(",");

/**
 * A control wider than this share of the viewport is a whole card or row
 * made clickable (a Discover card's link, a notification row). Its blank
 * background is not something a tap on the tab would steal; only its text
 * and inner controls are.
 */
export const WIDE_CONTROL_RATIO = 0.6;

/** Merge sorted-or-not [top, bottom] intervals into disjoint ones. */
export const mergeIntervals = (intervals) => {
  const sorted = (intervals || [])
    .filter((iv) => iv && iv[1] > iv[0])
    .map((iv) => [iv[0], iv[1]])
    .sort((a, b) => a[0] - b[0]);
  const out = [];
  sorted.forEach((iv) => {
    const last = out[out.length - 1];
    if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
    else out.push(iv);
  });
  return out;
};

/** How many px of [top, top + height] are covered by busy intervals. */
export const busyOverlap = (top, height, busy) =>
  (busy || []).reduce((sum, [a, b]) => {
    const ix = Math.min(b, top + height) - Math.max(a, top);
    return ix > 0 ? sum + ix : sum;
  }, 0);

/**
 * The top in [minTop, maxTop] whose column covers the least busy content,
 * ties broken by distance from `preferredTop` (the default corner or the
 * user's dragged spot). The preferred spot itself wins whenever it is clear,
 * so on a page with nothing under the corner the tab never moves.
 */
export const pickClearTop = ({ preferredTop, minTop, maxTop, height, busy, step = CANDIDATE_STEP }) => {
  const lo = Math.min(minTop, maxTop);
  const hi = Math.max(minTop, maxTop);
  const pref = Math.max(lo, Math.min(preferredTop, hi));
  const merged = mergeIntervals(busy);
  if (busyOverlap(pref, height, merged) === 0) return pref;
  let best = pref;
  let bestCost = busyOverlap(pref, height, merged);
  const consider = (t) => {
    const cost = busyOverlap(t, height, merged);
    if (cost < bestCost || (cost === bestCost && Math.abs(t - pref) < Math.abs(best - pref))) {
      best = t;
      bestCost = cost;
    }
  };
  for (let t = pref; t >= lo; t -= step) consider(t);
  for (let t = pref; t <= hi; t += step) consider(t);
  // The exact ends of the range are candidates too: the step may skip them.
  consider(lo);
  consider(hi);
  // A busy interval's own edges are where a clear gap starts or ends.
  merged.forEach(([a, b]) => {
    if (a - height >= lo && a - height <= hi) consider(a - height);
    if (b >= lo && b <= hi) consider(b);
  });
  return best;
};

const ownTextHits = (el, x, y, doc) => {
  if (typeof doc.createRange !== "function") return false;
  const nodes = el.childNodes || [];
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.nodeType === 3 && node.textContent.trim()) {
      const range = doc.createRange();
      range.selectNodeContents(node);
      const rects = typeof range.getClientRects === "function" ? range.getClientRects() : [];
      for (let j = 0; j < rects.length; j += 1) {
        const r = rects[j];
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true;
      }
    }
  }
  return false;
};

/**
 * Is the topmost page element at (x, y) something the tab must not cover?
 * A small control (or anything inside one) is; a line of text is; blank
 * background of a card, page or full-width clickable row is not.
 */
export const isBusyAt = (el, x, y, { doc, viewportWidth }) => {
  if (!el || el === doc.body || el === doc.documentElement) return false;
  const control = el.closest ? el.closest(CONTROL_SELECTOR) : null;
  if (control) {
    const r = control.getBoundingClientRect();
    if (r.width < viewportWidth * WIDE_CONTROL_RATIO) return true;
  }
  return ownTextHits(el, x, y, doc);
};

/**
 * Sample the right-edge column [viewportWidth - width, viewportWidth] from
 * yMin to yMax and return the merged busy intervals. `ignore` is the
 * launcher's own root, skipped so the tab never "sees" itself.
 */
export const sampleBusyIntervals = ({
  doc,
  viewportWidth,
  width,
  yMin,
  yMax,
  ignore,
  step = SAMPLE_STEP,
}) => {
  if (!doc || typeof doc.elementsFromPoint !== "function") return [];
  const xs = [viewportWidth - width + 4, viewportWidth - width / 2, viewportWidth - 4];
  const busy = [];
  for (let y = Math.max(0, yMin); y <= yMax; y += step) {
    const hit = xs.some((x) => {
      const stack = doc.elementsFromPoint(x, y) || [];
      const el = stack.find((e) => !(ignore && ignore.contains(e)));
      return isBusyAt(el, x, y, { doc, viewportWidth });
    });
    if (hit) busy.push([y - step / 2, y + step / 2]);
  }
  return mergeIntervals(busy);
};
